import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Grid } from '@mui/material'
import queryString from 'query-string'

import { useTitle } from 'hooks'

import LoginSidePanel from 'components/LoginSidePanel'

import { enabledFeatures } from 'config/env'
import backend, { isAuthenticated } from 'utils/backend'
import history from 'utils/history'
import { LOGIN } from 'utils/paths'

const MCP_OAUTH_RESUME_KEY = 'mcp-oauth-resume'

const OAuthAuthorizePage = () => {
  const { i18n } = useTranslation()
  const t = i18n.getResourceBundle(i18n.language)
  const { search } = useLocation()
  // 'continue' is a reserved keyword in JS, so we alias it
  const { continue: continueToken } = queryString.parse(search)

  const [status, setStatus] = useState('loading') // 'loading' | 'error'
  const [errorMessage, setErrorMessage] = useState('')

  useTitle(t => t.mcp.title)

  useEffect(() => {
    if (!enabledFeatures.includes('MCP_OAUTH')) {
      history.replace('/')
      return
    }

    if (!continueToken) {
      setStatus('error')
      setErrorMessage(t.mcp.invalidRequest)
      return
    }

    if (!isAuthenticated()) {
      // Stash the full URL so the login flow can resume here after authentication
      window.localStorage.setItem(
        MCP_OAUTH_RESUME_KEY,
        window.location.pathname + window.location.search
      )
      history.push(LOGIN)
      return
    }

    // User is authenticated — complete the OAuth handshake with the backend.
    // The backend OAuth endpoint lives at /oauth/authorize/complete, which is
    // a root-level route (no /api/ prefix). The intercepted `backend` axios
    // instance uses baseURL = REACT_APP_SERVER_BASE_URL (e.g. http://localhost:8000/).
    // Passing a path without a leading slash lets axios append it to the baseURL
    // normally. The request interceptor automatically attaches the Bearer token.
    const completeOAuth = async () => {
      try {
        const response = await backend.post('oauth/authorize/complete', {
          continue: continueToken
        })

        // Handle both snake_case (redirect_to) and camelCase (redirectTo) response keys
        const redirectUrl = response.data.redirectTo || response.data.redirect_to

        if (!redirectUrl) {
          setStatus('error')
          setErrorMessage(t.mcp.noRedirect)
          return
        }

        // The page never constructs the redirect itself — it only follows
        // the URL that the backend provides.
        window.location.assign(redirectUrl)
      } catch (err) {
        const serverMessage =
          err?.response?.data?.detail || err?.response?.data?.error || err?.message
        setStatus('error')
        setErrorMessage(
          serverMessage ? `${t.mcp.authFailed} ${serverMessage}` : t.mcp.unexpectedError
        )
      }
    }

    completeOAuth()
  }, [continueToken])

  if (status === 'error') {
    return (
      <div>
        <Grid container>
          <LoginSidePanel />
          <Grid size={{ xs: 12, md: 9 }}>
            <div className="password_reset_dialog">
              <b>{t.mcp.title}</b>
              <h3>{t.mcp.errorTitle}</h3>
              <div className="message-danger">{errorMessage}</div>
            </div>
          </Grid>
        </Grid>
      </div>
    )
  }

  // 'loading' state — the POST is in flight or we are about to redirect
  return (
    <div>
      <Grid container>
        <LoginSidePanel />
        <Grid size={{ xs: 12, md: 9 }}>
          <div className="password_reset_dialog">
            <b>{t.mcp.title}</b>
            <h3>{t.mcp.connectingTitle}</h3>
            <p>{t.mcp.connectingBody}</p>
          </div>
        </Grid>
      </Grid>
    </div>
  )
}

export default OAuthAuthorizePage
